<template>
	<el-button 
		type="primary" 
		size="small" 
		@click="onPrintClick"
		:disabled="!orderId || loading"
		:class="{ 'icon-only': iconOnly }">
		<el-icon><Printer /></el-icon>
		<span v-if="!iconOnly">&nbsp;Печать</span>
	</el-button>

	<!-- Santa hat animation -->
	<div v-if="showSantaHat" class="santa-hat-container">
		<div class="christmas-lights">
			<span class="light light-1"></span>
			<span class="light light-2"></span>
			<span class="light light-3"></span>
			<span class="light light-4"></span>
			<span class="light light-5"></span>
			<span class="light light-6"></span>
		</div>
		<div class="santa-hat">🎅</div>
		<div class="christmas-lights">
			<span class="light light-7"></span>
			<span class="light light-8"></span>
			<span class="light light-9"></span>
			<span class="light light-10"></span>
			<span class="light light-11"></span>
			<span class="light light-12"></span>
		</div>
	</div>

	<el-dialog 
		v-model="modalVisible" 
		@closed="onModalClosed" 
		width="600px" 
		:close-on-click-modal="false"
		:close-on-press-escape="true"
		append-to-body
		class="print-dialog">
		<template #header>
			<div style="display: flex; align-items: center; justify-content: space-between;">
				<h3 style="margin: 0">Печать заказ-наряда</h3>				
			</div>
		</template>
		
		<div v-loading="loading" class="print-content-container">
			<el-button :disabled="!printFormContent" type="primary" @click="onPrintButtonClick" style="margin: .6 0 1rem;">
				<el-icon><Printer /></el-icon>
				&nbsp;Печать
			</el-button>
			<iframe
				v-if="printFormContent"
				class="print-preview"
				:srcdoc="printFormContent"
				sandbox="allow-same-origin"
				ref="previewIframe"
			/>
			<div v-if="error" class="error-message">
				<el-alert
					title="Ошибка загрузки печатной формы"
					:description="error"
					type="error"
					:closable="false"
				/>
			</div>
		</div>
	</el-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { ElButton, ElDialog, ElAlert } from 'element-plus';
import { Printer } from '@element-plus/icons-vue';
import store from '@/store';
import axiosHelper from '@/helpers/axiosHelper';

@Options({
	components: {
		ElButton,
		ElDialog,
		ElAlert,
		Printer
	},
	props: {
		orderId: {
			type: Number,
			required: true
		},
		iconOnly: {
			type: Boolean,
			default: false
		}
	}
})
export default class OrderPrintFormComponent extends Vue {
	orderId!: number;

	modalVisible = false;
	loading = false;
	printFormContent: string | null = null;
	error: string | null = null;
	showSantaHat = false;
	iconOnly!: boolean;

	isNewYearSeason(): boolean {
		const now = new Date();
		const endDate = new Date(2026, 0, 8, 23, 59, 59); // January 8, 2026, 23:59:59
		return now <= endDate;
	}

	onPrintClick() {
		if (!this.orderId) {
			return;
		}

		// Show Santa hat animation only until January 8th, 2026
		if (this.isNewYearSeason()) {
			this.showSantaHat = true;
			setTimeout(() => {
				this.showSantaHat = false;
			}, 4000); // Show for 4 seconds
		}

		this.loading = true;
		this.error = null;
		this.printFormContent = null;
		this.modalVisible = true;

		this.fetchPrintForm();
	}

	async fetchPrintForm() {
		try {
			const jwt = store.getters.jwt;
			const axiosInstance = axiosHelper.createInstance(jwt);
			
			const response = await axiosInstance.get(`/api/PrintForm/order/${this.orderId}`, {
				responseType: 'text'
			});

			this.printFormContent = response.data;
			this.error = null;
		} catch (e: any) {
			if (e.response) {
				this.error = `Ошибка загрузки: ${e.response.status} ${e.response.statusText}`;
			} else if (e.request) {
				this.error = 'Ошибка сети при загрузке печатной формы';
			} else {
				this.error = 'Ошибка при загрузке печатной формы';
			}
			this.printFormContent = null;
			console.error('Error fetching print form:', e);
		} finally {
			this.loading = false;
		}
	}

	onPrintButtonClick() {
		if (!this.printFormContent) {
			return;
		}

		const iframe = document.createElement('iframe');
		iframe.style.position = 'fixed';
		iframe.style.right = '0';
		iframe.style.bottom = '0';
		iframe.style.width = '0';
		iframe.style.height = '0';
		iframe.style.border = '0';
		iframe.style.opacity = '0';
		iframe.style.pointerEvents = 'none';		
		iframe.srcdoc = this.printFormContent;
		
		document.body.appendChild(iframe);

		let hasPrinted = false;

		const printIframe = () => {
			if (hasPrinted) {
				return;
			}
			hasPrinted = true;

			const iframeWindow = iframe.contentWindow;
			const iframeDoc = iframe.contentDocument || iframeWindow?.document;
			
			if (iframeWindow && iframeDoc) {
                iframeDoc.title = ' ';
				const style = iframeDoc.createElement('style');
				style.textContent = `
					@page {
						size: auto;
						margin: 0mm 10mm;
					}`;
				const head = iframeDoc.head || iframeDoc.getElementsByTagName('head')[0] || iframeDoc.body;
				if (head) {
					head.appendChild(style);
				}

				// Use print settings to hide headers/footers
				iframeWindow.focus();
				
				// Trigger print
				setTimeout(() => iframeWindow.print(), 50);
				
				// Clean up: remove iframe after a delay
				setTimeout(() => {
					if (iframe.parentNode) {
						document.body.removeChild(iframe);
					}
				}, 1000);
			}
		};

		// Wait for iframe to load
		iframe.onload = () => setTimeout(printIframe, 100);

		// Fallback: if onload doesn't fire, try printing after a short delay
		setTimeout(() => {
			const iframeDoc = iframe.contentDocument || (iframe.contentWindow as Window)?.document;
			if (iframeDoc && iframeDoc.readyState === 'complete' && !hasPrinted) {
				printIframe();
			}
		}, 500);
	}

	onModalClosed() {
		this.printFormContent = null;
		this.error = null;
	}
}
</script>

<style scoped>
.print-content-container {
	min-height: 400px; 
	max-height: 600px;
	max-width: 800px;
	width: 100%;
	margin: 0 auto;
	overflow-y: auto;
}

.print-preview {
	width: 100%;
	min-height: 400px;
	margin-top: 1rem;
	border: 0;
	outline: 0;
	box-sizing: border-box;
	background: var(--el-fill-color-blank);
}

.error-message {
	margin-top: 20px;
}

/* Print dialog z-index fix */
:deep(.print-dialog) {
	z-index: 3001 !important;
}

:deep(.print-dialog .el-overlay) {
	z-index: 3000 !important;
}

/* Icon-only button style */
.el-button.icon-only {
	padding: 8px;
	min-width: auto;
}

.el-button.icon-only .el-icon {
	margin: 0;
}

/* Santa hat animation */
.santa-hat-container {
	position: fixed;
	top: 80px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 4000;
	pointer-events: none;
	animation: santaHatSlideDown 0.8s ease-out;
	text-align: center;
}

.santa-hat {
	font-size: 60px;
	animation: santaHatBounce 0.5s ease-in-out 0.8s, santaHatWiggle 0.3s ease-in-out 1.3s 3;
	transform-origin: center bottom;
	display: block;
	margin: 1.5rem 0;
}

/* Christmas lights */
.christmas-lights {
	display: flex;
	justify-content: center;
	gap: 15px;
	margin: 5px 0;
}

.light {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	display: inline-block;
	box-shadow: 0 0 10px currentColor, 0 0 20px currentColor, 0 0 30px currentColor;
	animation: twinkle 1.5s ease-in-out infinite;
}

.light-1 { background: #ff0000; color: #ff0000; animation-delay: 0s; }
.light-2 { background: #00ff00; color: #00ff00; animation-delay: 0.2s; }
.light-3 { background: #0000ff; color: #0000ff; animation-delay: 0.4s; }
.light-4 { background: #ffff00; color: #ffff00; animation-delay: 0.6s; }
.light-5 { background: #ff00ff; color: #ff00ff; animation-delay: 0.8s; }
.light-6 { background: #00ffff; color: #00ffff; animation-delay: 1s; }
.light-7 { background: #ff8800; color: #ff8800; animation-delay: 0.1s; }
.light-8 { background: #8800ff; color: #8800ff; animation-delay: 0.3s; }
.light-9 { background: #ff0088; color: #ff0088; animation-delay: 0.5s; }
.light-10 { background: #88ff00; color: #88ff00; animation-delay: 0.7s; }
.light-11 { background: #0088ff; color: #0088ff; animation-delay: 0.9s; }
.light-12 { background: #ff4444; color: #ff4444; animation-delay: 1.1s; }

@keyframes twinkle {
	0%, 100% {
		opacity: 0.3;
		transform: scale(0.8);
	}
	50% {
		opacity: 1;
		transform: scale(1.2);
	}
}

@keyframes santaHatSlideDown {
	0% {
		top: -100px;
		opacity: 0;
	}
	100% {
		top: 80px;
		opacity: 1;
	}
}

@keyframes santaHatBounce {
	0%, 100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-20px);
	}
}

@keyframes santaHatWiggle {
	0%, 100% {
		transform: rotate(0deg);
	}
	25% {
		transform: rotate(-10deg);
	}
	75% {
		transform: rotate(10deg);
	}
}

/* Responsive: adjust for smaller screens */
@media (max-width: 900px) {
	.print-content-container {
		max-width: calc(100vw - 80px);
		max-height: calc(100vh - 200px);
	}
}

@media (max-width: 600px) {
	.print-content-container {
		max-width: calc(100vw - 40px);
		max-height: calc(100vh - 180px);
	}
}

</style>

